import React from 'react'
import { FBPage } from 'components/FbUI/Page/FBPage'
import { NewFooter } from 'components/Layout/Footer/NewFooter'
import { CompanySidebarNew } from './Sidebar/CompanySidebarNew'
import type { CompanySidebarHeader, SidebarTabConfig } from './Sidebar'
import {
  useHideFooter,
  useEntityBreadcrumbs,
  useDynamicCategoryInfo,
} from 'context/layout'
import { CompanyPageProvider } from './CompanyPageContext'
import type { BreadcrumbItem } from 'components/Layout/Header/types'

const EMPTY_BREADCRUMBS: BreadcrumbItem[] = []

interface CompanyPageLayoutNewProps {
  chainProxyId: number
  sidebar: {
    header: CompanySidebarHeader
    tabs: SidebarTabConfig[]
  }
  doorId?: number
  address?: string
  phone?: string
  children?: React.ReactNode
  /** Entity breadcrumbs for new navigation header */
  entityBreadcrumbs?: BreadcrumbItem[]
  /** Dynamic category info for unmatched/user-generated pages */
  categoryInfo?: {
    categorySlug: string
    subCategorySlug: string
  }
}

/**
 * New company page layout for new navigation.
 * @see TODO in NewCompanyPage/index.tsx for cleanup steps when old nav is removed.
 */
export function CompanyPageLayoutNew(props: CompanyPageLayoutNewProps) {
  const { chainProxyId, entityBreadcrumbs, categoryInfo } = props

  // Hide footer on company pages (new nav has its own footer handling)
  useHideFooter()

  // Set entity breadcrumbs in header (e.g., "Chain: McDonald's | Door: Store #123")
  // Use stable empty array reference to prevent unnecessary re-renders
  useEntityBreadcrumbs(entityBreadcrumbs ?? EMPTY_BREADCRUMBS)

  // Set dynamic category info for unmatched/user-generated pages
  useDynamicCategoryInfo(
    categoryInfo?.categorySlug,
    categoryInfo?.subCategorySlug
  )

  return (
    <FBPage className="gap-y-4" noContainer innerPageClassName="!pb-0 !pr-0">
      {/* Content area - flex container that fills available height */}
      <div className="flex gap-6 flex-1 min-h-0 overflow-hidden">
        <CompanySidebarNew
          chainProxyId={chainProxyId}
          {...props.sidebar}
          doorId={props.doorId}
          address={props.address}
          phone={props.phone}
        />

        {/* Right content area - scroll container flush with right edge */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <div className="flex flex-col gap-6 mr-5 min-h-full">
            <CompanyPageProvider mode="new">
              {props.children}
            </CompanyPageProvider>
            <div className="mt-auto">
              <NewFooter />
            </div>
          </div>
        </div>
      </div>
    </FBPage>
  )
}
